<template>
  <div class="tm-scope tm-cv">
    <div v-if="empty" class="tm-cv-empty tm-reveal">
      <h3 class="tm-cv-empty__title">Your CV hasn't been uploaded yet</h3>
      <button class="tm-btn-primary mt-3" type="button" data-bs-toggle="modal" data-bs-target="#exampleModal1">Upload your CV</button>
    </div>

    <!-- Parsing is the most impressive thing the product does; show the work
         rather than a spinner. Stages advance on a timer against the request
         that's already in flight, and hold on the last one until it lands.
         Only shown for an actual fresh upload — parsingFileName is only set
         when a file is actively being parsed (see startFeed). -->
    <div v-else-if="spinner && !empty && parsingFileName" class="tm-container tm-container--text tm-cv-parsing">
      <span class="tm-eyebrow">Reading {{ parsingFileName }}</span>
      <h2 class="tm-cv-parsing__title">Extracting your career</h2>

      <div class="tm-meter tm-cv-parsing__progress">
        <span class="tm-meter__fill tm-meter__fill--gradient"
              :style="{ width: ((stage + 1) / stages.length * 100) + '%' }"></span>
      </div>

      <div class="tm-feed">
        <div
          v-for="(s, i) in stages" :key="s"
          class="tm-feed__row"
          :class="{ on: i === stage, done: i < stage }"
        >
          <span class="tm-feed__tick">&check;</span>
          <span class="tm-feed__label">{{ s }}</span>
        </div>
      </div>
    </div>

    <!-- Fetching an already-parsed CV from the DB — a plain loader, never
         the extraction narrative above, so it can't read as "re-parsing". -->
    <div v-else-if="spinner && !empty" class="tm-cv-loading">
      <Spinner :size="28" />
      <p class="tm-muted mt-3 mb-0">Loading your CV&hellip;</p>
    </div>

    <div v-else class="tm-dash-shell">
      <div class="tm-dash-shell__inner tm-cv-wizard">
        <div class="tm-cv-head tm-reveal">
          <div>
            <h1 class="tm-page-title tm-page-title--tight">Your CV</h1>
          </div>
          <div class="tm-cv-head__actions">
            <button class="tm-btn-secondary" type="button" data-bs-toggle="modal" data-bs-target="#exampleModal1">Re-upload</button>
          </div>
        </div>

        <div class="tm-dash-grid">
          <!-- Step sidebar -->
          <div class="tm-card tm-cv-sidebar tm-reveal">
            <button
              v-for="(step, i) in steps" :key="step.key"
              type="button"
              class="tm-cv-sidebar__step"
              :class="{ active: i === currentStep }"
              :disabled="!stepsUnlocked && i > currentStep"
              @click="goToStep(i)"
            >
              <span class="tm-cv-sidebar__num tm-mono">{{ String(i + 1).padStart(2, '0') }}</span>
              <span class="tm-cv-sidebar__label">{{ step.label }}</span>
            </button>
          </div>

          <div class="tm-cv-main">
      <transition name="tm-step" mode="out-in">
        <!-- Step 1: Personal info -->
        <div v-if="steps[currentStep].key === 1" key="1" class="tm-cv-step">
          <div class="tm-personal-grid">
            <div class="tm-card">
              <h4 class="mb-4">Personal information</h4>

              <div class="tm-field-grid">
                <div>
                  <label class="tm-field-label" for="pf-name">Full name <span class="tm-required">*</span></label>
                  <input id="pf-name" type="text" class="tm-field" :class="{ 'tm-field--error': personalTouched && !personal.name }"
                         placeholder="e.g. Alexander Sterling" v-model="personal.name" @blur="onPersonalBlur('name', personal.name)">
                </div>
                <div>
                  <label class="tm-field-label" for="pf-title">Professional title</label>
                  <input id="pf-title" type="text" class="tm-field" placeholder="e.g. Senior Product Designer"
                         v-model="personal.title" @blur="onPersonalBlur('professional_title', personal.title)">
                </div>
                <div>
                  <label class="tm-field-label" for="pf-email">Email address <span class="tm-required">*</span></label>
                  <input id="pf-email" type="email" class="tm-field" :class="{ 'tm-field--error': personalTouched && !personal.email }"
                         placeholder="you@example.com" v-model="personal.email" @blur="onPersonalBlur('email', personal.email)">
                </div>
                <div>
                  <label class="tm-field-label" for="pf-phone">Phone number</label>
                  <input id="pf-phone" type="tel" class="tm-field" placeholder="+1 (555) 000-0000"
                         v-model="personal.phone" @blur="onPersonalBlur('mobile_number', personal.phone)">
                </div>
                <div class="tm-field-grid__full">
                  <label class="tm-field-label" for="pf-location">Location</label>
                  <input id="pf-location" type="text" class="tm-field" placeholder="City, Country"
                         v-model="personal.location" @blur="onPersonalBlur('address', personal.location)">
                </div>
              </div>
              <p v-if="personalTouched && (!personal.name || !personal.email)" class="tm-field-error mt-3">
                Full name and email are required before you can continue.
              </p>
            </div>

            <div class="tm-card tm-photo-panel">
              <div class="tm-photo-panel__frame">
                <img v-if="photoPreviewUrl" :src="photoPreviewUrl" alt="Profile" class="tm-photo-panel__img">
                <FontAwesomeIcon v-else icon="fa-solid fa-user" class="tm-photo-panel__placeholder" />
              </div>
              <h5 class="tm-photo-panel__title">Profile image</h5>
              <button type="button" class="tm-btn-ghost tm-photo-panel__change" @click="$refs.photoInput.click()">Change photo</button>
              <input ref="photoInput" type="file" accept="image/*" class="d-none" @change="onManualPhotoSelected">

              <div v-if="photoConfirmPending" class="tm-photo-confirm">
                <p>We found this photo in your CV. Use it as your account profile picture?</p>
                <div class="tm-photo-confirm__actions">
                  <button type="button" class="tm-btn-secondary" @click="dismissExtractedPhoto()">Not now</button>
                  <button type="button" class="tm-btn-primary" :disabled="photoSaving" @click="confirmExtractedPhoto()">
                    <Spinner v-if="photoSaving" :size="14" /><span v-else>Use this photo</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Experience -->
        <div v-else-if="steps[currentStep].key === 2" key="2" class="tm-card tm-cv-step">
          <div class="tm-section-head tm-section-head--in-card">
            <div>
              <h4 class="mb-0">Previous experience</h4>
            </div>
            <button type="button" class="tm-btn-secondary" @click="openExperienceModal('add')">
              <FontAwesomeIcon icon="fa-solid fa-circle-plus" /> Add experience
            </button>
          </div>

          <p v-if="!experienceItems.length" class="tm-cv-step__empty">No experience found yet — add one to get started.</p>

          <div v-else class="tm-exp-list">
            <div v-for="exp in experienceItems" :key="exp.id" class="tm-exp-row">
              <span v-if="exp.startDate || exp.endDate" class="tm-exp-row__dates tm-mono">
                {{ exp.startDate || '—' }} &mdash; {{ exp.endDate || 'Present' }}
              </span>
              <div class="tm-exp-row__body">
                <h5 class="tm-exp-row__title">
                  {{ exp.title || 'Untitled role' }}
                  <span
                    v-if="exp.unmatched" class="tm-unmatched-badge"
                    title="Not yet in our data — recorded, but won't count toward career-path matching yet"
                  >not yet matched</span>
                </h5>
                <span v-if="exp.company || exp.employmentType" class="tm-exp-row__meta">
                  {{ exp.company }}<template v-if="exp.company && exp.employmentType"> &middot; </template>{{ exp.employmentType }}
                </span>
                <p v-if="exp.description" class="tm-exp-row__desc">{{ exp.description }}</p>
                <div class="tm-exp-row__actions">
                  <button type="button" class="tm-icon-action" title="Edit" @click="openExperienceModal('edit', exp)">
                    <FontAwesomeIcon icon="fa-solid fa-pen" />
                  </button>
                  <button type="button" class="tm-icon-action tm-icon-action--danger" title="Remove" @click="removeExperience(exp)">
                    <FontAwesomeIcon icon="fa-solid fa-trash-can" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Skills — unchanged -->
        <div v-else-if="steps[currentStep].key === 3" key="3" class="tm-card tm-cv-step">
          <h4 class="mb-4">Skills & knowledge</h4>
          <div class="tm-chip-zone">
            <div
              v-for="item in listThree" :key="item.id" class="tm-chip-item"
              :class="{ 'tm-chip-item--unmatched': item.unmatched, 'tm-chip-item--verified': item.source === 'course' && item.verified }"
              :title="item.unmatched ? 'Not yet in our data — recorded, but won\'t count toward career-path matching yet' : null"
            >
              <Pill
                editable solid
                :model-value="item.title"
                :input-width="item.title.length>12 ? item.title.length + 'ch' : item.title.length + 2 + 'ch'"
                @update:modelValue="item.title = $event"
                @click="item.showDropdown = !item.showDropdown"
                @blur="handleBlur1(item)"
                @remove="removeSkill(item)"
              />
              <span v-if="item.source === 'course'" class="tm-chip-item__src">{{ item.verified ? 'Course · verified' : 'Course' }}</span>
              <SuggestionDropdown
                :items="item.other_results && item.other_results.length ? item.other_results[0] : []"
                :visible="item.showDropdown"
                @select="(option) => selectOption(option, item)"
              />
            </div>
          </div>
          <button type="button" class="tm-btn-ghost mt-3" data-bs-toggle="modal" data-bs-target="#exampleModal" @click="setNumber(3)">+ Add item</button>
        </div>

        <!-- Step 4: Certificates & Languages / Interests -->
        <div v-else-if="steps[currentStep].key === 4" key="4" class="tm-cv-step">
          <div class="tm-card">
            <h4 class="mb-3">Certificates</h4>
            <p v-if="!certificateItems.length" class="tm-cv-step__empty">No certificates added yet.</p>
            <div v-else class="tm-cert-list">
              <div v-for="cert in certificateItems" :key="cert.id" class="tm-cert-row">
                <span class="tm-icon-tile"><FontAwesomeIcon icon="fa-solid fa-award" /></span>
                <div class="tm-cert-row__body">
                  <h6 class="tm-cert-row__name">{{ cert.name }}</h6>
                  <span class="tm-cert-row__meta tm-mono" v-if="cert.issuer || cert.year">
                    {{ (cert.issuer || '').toUpperCase() }}<template v-if="cert.issuer && cert.year"> &middot; </template>{{ cert.year }}
                  </span>
                </div>
                <button type="button" class="tm-icon-action tm-icon-action--danger" title="Remove" @click="removeCertificate(cert)">
                  <FontAwesomeIcon icon="fa-solid fa-trash-can" />
                </button>
              </div>
            </div>
            <button type="button" class="tm-add-dashed mt-3" @click="openCertificateModal()">+ Add certificate</button>
          </div>

          <div class="tm-lang-interest-grid mt-5">
            <div class="tm-card">
              <h4 class="mb-3">Languages</h4>
              <div class="tm-lang-grid">
                <div v-for="lang in languageItems" :key="lang.id" class="tm-lang-item">
                  <div class="tm-lang-item__row">
                    <span class="tm-lang-item__name">{{ lang.name }}</span>
                    <span class="tm-lang-item__level">{{ lang.proficiency }}</span>
                    <button type="button" class="tm-icon-action tm-icon-action--danger tm-lang-item__remove" title="Remove" @click="removeLanguage(lang)">
                      <FontAwesomeIcon icon="fa-solid fa-trash-can" />
                    </button>
                  </div>
                  <span class="tm-meter">
                    <span class="tm-meter__fill" :style="{ width: proficiencyPct(lang.proficiency) + '%' }"></span>
                  </span>
                </div>
              </div>
              <button type="button" class="tm-btn-ghost mt-3" @click="openLanguageModal()">+ Add language</button>
            </div>

            <div class="tm-card">
              <h4 class="mb-3">Interests</h4>
              <div v-if="interestItems.length" class="tm-interest-grid">
                <div v-for="(interest, i) in interestItems" :key="interest + i" class="tm-interest-chip">
                  <button v-if="manageInterests" type="button" class="tm-interest-chip__remove" @click="removeInterest(i)" aria-label="Remove">
                    <FontAwesomeIcon icon="fa-solid fa-xmark" />
                  </button>
                  <span class="tm-icon-tile"><FontAwesomeIcon :icon="'fa-solid ' + interestIcon(interest)" /></span>
                  <span>{{ interest }}</span>
                </div>
              </div>
              <p v-else class="tm-muted" style="font-size: 0.85rem;">No interests added yet.</p>

              <div v-if="manageInterests" class="tm-interest-add">
                <input type="text" class="tm-field" placeholder="e.g. Photography" v-model="newInterestText" @keyup.enter="addInterest()">
                <button type="button" class="tm-btn-secondary" @click="addInterest()">Add</button>
              </div>
              <button type="button" class="tm-btn-secondary tm-manage-interests" @click="manageInterests = !manageInterests">
                {{ manageInterests ? 'Done' : 'Manage interests' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Step 5: Review — each section reuses its real step's box design,
             read-only. -->
        <div v-else key="review" class="tm-cv-step">
          <div class="tm-review-stack">
            <div class="tm-card">
              <h4 class="mb-3">Personal information</h4>
              <div class="tm-review-personal">
                <img v-if="photoPreviewUrl" :src="photoPreviewUrl" alt="" class="tm-review-personal__photo">
                <FontAwesomeIcon v-else icon="fa-solid fa-user" class="tm-review-personal__photo tm-review-personal__photo--placeholder" />
                <div class="tm-review-personal__name-block">
                  <div class="tm-review-personal__name">{{ personal.name || '—' }}</div>
                  <div v-if="personal.title" class="tm-muted">{{ personal.title }}</div>
                </div>
              </div>
              <div class="tm-field-grid mt-4">
                <div>
                  <span class="tm-field-label">Email address</span>
                  <p class="tm-review-value">{{ personal.email || '—' }}</p>
                </div>
                <div>
                  <span class="tm-field-label">Phone number</span>
                  <p class="tm-review-value">{{ personal.phone || '—' }}</p>
                </div>
                <div class="tm-field-grid__full">
                  <span class="tm-field-label">Location</span>
                  <p class="tm-review-value">{{ personal.location || '—' }}</p>
                </div>
              </div>
            </div>

            <div class="tm-card">
              <h4 class="mb-3">Previous experience</h4>
              <p v-if="!experienceItems.length" class="tm-cv-step__empty">None added.</p>
              <div v-else class="tm-exp-list">
                <div v-for="exp in experienceItems" :key="exp.id" class="tm-exp-row">
                  <span v-if="exp.startDate || exp.endDate" class="tm-exp-row__dates tm-mono">
                    {{ exp.startDate || '—' }} &mdash; {{ exp.endDate || 'Present' }}
                  </span>
                  <div class="tm-exp-row__body">
                    <h5 class="tm-exp-row__title">{{ exp.title || 'Untitled role' }}</h5>
                    <span v-if="exp.company || exp.employmentType" class="tm-exp-row__meta">
                      {{ exp.company }}<template v-if="exp.company && exp.employmentType"> &middot; </template>{{ exp.employmentType }}
                    </span>
                    <p v-if="exp.description" class="tm-exp-row__desc">{{ exp.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="tm-card">
              <h4 class="mb-3">Skills & knowledge</h4>
              <p v-if="!listThree.length" class="tm-cv-step__empty">None added.</p>
              <div v-else class="tm-chip-zone">
                <span v-for="item in listThree" :key="item.id" class="tm-tag">{{ item.title }}</span>
              </div>
            </div>

            <div class="tm-card">
              <h4 class="mb-3">Certificates</h4>
              <p v-if="!certificateItems.length" class="tm-cv-step__empty">None added.</p>
              <div v-else class="tm-cert-list">
                <div v-for="cert in certificateItems" :key="cert.id" class="tm-cert-row">
                  <span class="tm-icon-tile"><FontAwesomeIcon icon="fa-solid fa-award" /></span>
                  <div class="tm-cert-row__body">
                    <h6 class="tm-cert-row__name">{{ cert.name }}</h6>
                    <span class="tm-cert-row__meta tm-mono" v-if="cert.issuer || cert.year">
                      {{ (cert.issuer || '').toUpperCase() }}<template v-if="cert.issuer && cert.year"> &middot; </template>{{ cert.year }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="tm-lang-interest-grid">
              <div class="tm-card">
                <h4 class="mb-3">Languages</h4>
                <p v-if="!languageItems.length" class="tm-cv-step__empty">None added.</p>
                <div v-else class="tm-lang-grid">
                  <div v-for="lang in languageItems" :key="lang.id" class="tm-lang-item">
                    <div class="tm-lang-item__row">
                      <span class="tm-lang-item__name">{{ lang.name }}</span>
                      <span class="tm-lang-item__level">{{ lang.proficiency }}</span>
                    </div>
                    <span class="tm-meter">
                      <span class="tm-meter__fill" :style="{ width: proficiencyPct(lang.proficiency) + '%' }"></span>
                    </span>
                  </div>
                </div>
              </div>

              <div class="tm-card">
                <h4 class="mb-3">Interests</h4>
                <p v-if="!interestItems.length" class="tm-cv-step__empty">None added.</p>
                <div v-else class="tm-interest-grid">
                  <div v-for="(interest, i) in interestItems" :key="interest + i" class="tm-interest-chip">
                    <span class="tm-icon-tile"><FontAwesomeIcon :icon="'fa-solid ' + interestIcon(interest)" /></span>
                    <span>{{ interest }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button class="tm-btn-primary w-100 mt-4" @click="router.push({'name':'dashboard-careers'})">My Career Path</button>
        </div>
      </transition>

            <div class="tm-cv-nav tm-reveal">
              <button class="tm-btn-secondary" :disabled="currentStep === 0" @click="goBack()">Back</button>
              <button v-if="currentStep < steps.length - 1" class="tm-btn-primary" @click="saveCurrentStep()">Save &amp; continue</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: add a skill (unchanged) -->
      <div class="modal fade" id="exampleModal" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog">
          <div class="modal-content tm-modal">
            <div class="modal-header">
              <h1 class="modal-title fs-5" id="exampleModalLabel">Add item</h1>
              <button id="closeModal" type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" @click="newItem=''; newItemId=null"></button>
            </div>
            <div class="modal-body">
              <div class="tm-modal-input-wrap">
                <input type="text" class="tm-field" placeholder="Add new item" v-model="newItem" @input="newItemId = null">
                <SuggestionDropdown :items="list" label-key="name" :visible="showdropdown" @select="setTitle" />
              </div>
              <div v-if="error2" class="text-danger mt-2" style="font-size: 0.85rem;">{{ error2 }}</div>
            </div>
            <div class="modal-footer">
              <button type="button" class="tm-btn-primary" @click="addNewItem()">Add</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal: upload CV (unchanged) -->
    <div class="modal fade" id="exampleModal1" tabindex="-1" aria-labelledby="exampleModalLabel1" aria-hidden="true">
        <div class="modal-dialog">
            <div class="modal-content tm-modal">
                <div class="modal-header">
                    <h1 class="modal-title fs-5" id="exampleModalLabel1">CV upload</h1>
                    <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body text-center">
                    <div>
                        <div
                          class="tm-dropzone"
                          :class="{ 'tm-dropzone--active': isDragOver }"
                          @dragover.prevent="isDragOver = true"
                          @dragenter.prevent="isDragOver = true"
                          @dragleave.prevent="isDragOver = false"
                          @drop="isDragOver = false; handleDrop($event)"
                        >
                        <div v-if="preview">
                            <FontAwesomeIcon icon="fa-solid fa-file-upload" class="tm-dropzone__icon"></FontAwesomeIcon>
                            <div class="mt-2">{{ preview }}</div>
                        </div>
                        <div v-else>
                            <p class="tm-muted mb-0">Drag and drop the file here</p>
                        </div>
                    </div>
                    <div v-if="error" class="text-danger mt-2">{{ error }}</div>
                    <input type="file" ref="fileInput" @change="handleFileSelect($event)"
                            accept="application/pdf,application/msword,image/jpeg"/>
                    </div>
                </div>
                <div class="modal-footer">
                    <button id="close" type="button" class="tm-btn-secondary" data-bs-dismiss="modal">Close</button>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal: add/edit experience -->
    <div class="modal fade" id="experienceModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content tm-modal">
          <div class="modal-header">
            <h1 class="modal-title fs-5">{{ expModalMode === 'edit' ? 'Edit experience' : 'Add experience' }}</h1>
            <button id="closeExperienceModal" type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" @click="expError = ''"></button>
          </div>
          <div class="modal-body">
            <div class="tm-field-grid">
              <div class="tm-field-grid__full tm-modal-input-wrap">
                <label class="tm-field-label">Job title</label>
                <input type="text" class="tm-field" placeholder="e.g. Product Designer" v-model="expDraft.title"
                       @input="expDraft.titleConfirmed = false; onExpTitleInput()"
                       @focus="expDraft.showDropdown = true" @blur="onExpTitleBlur">
                <SuggestionDropdown
                  :items="expDraft.other_results && expDraft.other_results.length ? expDraft.other_results[0] : []"
                  :visible="expDraft.showDropdown"
                  @select="(option) => { expDraft.title = option; expDraft.titleConfirmed = true; expDraft.showDropdown = false; }"
                />
                <div v-if="expError" class="text-danger mt-2" style="font-size: 0.85rem;">{{ expError }}</div>
              </div>
              <div>
                <label class="tm-field-label">Company</label>
                <input type="text" class="tm-field" placeholder="e.g. Acme Corp" v-model="expDraft.company">
              </div>
              <div>
                <label class="tm-field-label">Employment type</label>
                <select class="tm-field" v-model="expDraft.employmentType">
                  <option value="">Not specified</option>
                  <option v-for="opt in employmentTypes" :key="opt" :value="opt">{{ opt }}</option>
                </select>
              </div>
              <div>
                <label class="tm-field-label">Start date</label>
                <input type="text" class="tm-field" placeholder="e.g. Jan 2021" v-model="expDraft.startDate">
              </div>
              <div>
                <label class="tm-field-label">End date</label>
                <input type="text" class="tm-field" placeholder="Present" v-model="expDraft.endDate">
              </div>
              <div class="tm-field-grid__full">
                <label class="tm-field-label">Description</label>
                <textarea class="tm-field" rows="3" placeholder="What did this role involve?" v-model="expDraft.description"></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="tm-btn-primary" @click="saveExperienceModal()">Save</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: add certificate -->
    <div class="modal fade" id="certificateModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content tm-modal">
          <div class="modal-header">
            <h1 class="modal-title fs-5">Add certificate</h1>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="tm-field-grid">
              <div class="tm-field-grid__full">
                <label class="tm-field-label">Certificate name</label>
                <input type="text" class="tm-field" placeholder="e.g. AWS Cloud Practitioner" v-model="certDraft.name">
              </div>
              <div>
                <label class="tm-field-label">Issuer</label>
                <input type="text" class="tm-field" placeholder="e.g. Amazon" v-model="certDraft.issuer">
              </div>
              <div>
                <label class="tm-field-label">Year</label>
                <input type="text" class="tm-field" placeholder="e.g. 2023" v-model="certDraft.year">
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="tm-btn-primary" data-bs-dismiss="modal" @click="saveCertificateModal()">Add</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: add language -->
    <div class="modal fade" id="languageModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content tm-modal">
          <div class="modal-header">
            <h1 class="modal-title fs-5">Add language</h1>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="tm-field-grid">
              <div>
                <label class="tm-field-label">Language</label>
                <input type="text" class="tm-field" placeholder="e.g. Spanish" v-model="langDraft.name">
              </div>
              <div>
                <label class="tm-field-label">Proficiency</label>
                <select class="tm-field" v-model="langDraft.proficiency">
                  <option v-for="opt in proficiencyLevels" :key="opt" :value="opt">{{ opt }}</option>
                </select>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="tm-btn-primary" data-bs-dismiss="modal" @click="saveLanguageModal()">Add</button>
          </div>
        </div>
      </div>
    </div>
  </div>
  </template>

  <script>
import { CareerApiService } from '@/services/CareerApiService';
import { AuthApiService } from '@/services/AuthApiService';
import { onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  faClose, faFileUpload, faCirclePlus, faPen, faTrashCan, faAward, faUser, faXmark,
  faPalette, faMusic, faPersonRunning, faDumbbell, faBook, faPlane, faGamepad,
  faMicrochip, faCamera, faHandHoldingHeart, faUtensils, faPersonHiking, faChess, faRobot, faStar,
} from '@fortawesome/free-solid-svg-icons'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { UserInfoApiService } from '@/services/UserInfoApiService';
import Pill from '@/components/Pill.vue';
import Spinner from '@/components/Spinner.vue';
import SuggestionDropdown from '@/components/SuggestionDropdown.vue';

// Loose keyword match against common interest categories — good enough for
// "pick a sensible icon", not meant to be an exhaustive taxonomy.
const INTEREST_ICON_MAP = {
  art: 'fa-palette', design: 'fa-palette', paint: 'fa-palette',
  music: 'fa-music', instrument: 'fa-music',
  sport: 'fa-person-running', run: 'fa-person-running', football: 'fa-person-running', soccer: 'fa-person-running',
  fitness: 'fa-dumbbell', gym: 'fa-dumbbell', workout: 'fa-dumbbell',
  read: 'fa-book', book: 'fa-book', literature: 'fa-book',
  travel: 'fa-plane', wander: 'fa-plane',
  game: 'fa-gamepad', gaming: 'fa-gamepad',
  tech: 'fa-microchip', robot: 'fa-robot', ai: 'fa-microchip', code: 'fa-microchip', programming: 'fa-microchip',
  photo: 'fa-camera',
  volunteer: 'fa-hand-holding-heart', charity: 'fa-hand-holding-heart',
  cook: 'fa-utensils', food: 'fa-utensils', baking: 'fa-utensils',
  hik: 'fa-person-hiking', outdoor: 'fa-person-hiking', wilderness: 'fa-person-hiking', camp: 'fa-person-hiking',
  chess: 'fa-chess',
};
function interestIcon(name) {
  const key = (name || '').toLowerCase();
  for (const keyword in INTEREST_ICON_MAP) {
    if (key.includes(keyword)) return INTEREST_ICON_MAP[keyword];
  }
  return 'fa-star';
}

  export default {
    components: {FontAwesomeIcon, Pill, Spinner, SuggestionDropdown},
    setup() {
      library.add(
        faClose, faFileUpload, faCirclePlus, faPen, faTrashCan, faAward, faUser, faXmark,
        faPalette, faMusic, faPersonRunning, faDumbbell, faBook, faPlane, faGamepad,
        faMicrochip, faCamera, faHandHoldingHeart, faUtensils, faPersonHiking, faChess, faRobot, faStar,
      )
      const router = useRouter();
      const spinner = ref(true);
      const empty = ref(false)
      const extracted_text = ref()
      const item_id = ref(0)
      const selectedFile = ref(null)
      const preview = ref(null);
      const error = ref();
      const error2 = ref();
      const numberItem = ref();
      const newItem = ref();
      const list = ref();
      const showdropdown = ref(false);
      const cvId = ref();

      // Extraction feed. Stages advance on a timer and stop on the last one,
      // which then holds until the real request resolves and clears `spinner`.
      const stages = [
        'Reading the document',
        'Finding work experience',
        'Extracting skills',
        'Matching to the ESCO taxonomy',
        'Scoring against your target role',
      ];
      const stage = ref(0);
      const parsingFileName = ref('');
      let stageTimers = [];

      function startFeed(fileName) {
        parsingFileName.value = fileName || '';
        stage.value = 0;
        stageTimers.forEach(clearTimeout);
        stageTimers = [];
        for (let i = 1; i < stages.length; i++) {
          stageTimers.push(setTimeout(() => { stage.value = i }, i * 3200));
        }
      }

      function stopFeed() {
        stageTimers.forEach(clearTimeout);
        stageTimers = [];
      }

      onUnmounted(stopFeed);

      const newItemId = ref(null);

      function setTitle(item){
          newItem.value = item.name
          newItemId.value = item.id
          setTimeout(() => {
              showdropdown.value = false
          }, 500);
      }

      watch(newItem,()=>{
        error2.value = false;
        if(newItem.value==''){
          list.value = [];
          showdropdown.value = true;
        }
        else if (newItem.value!=''&&numberItem.value==3) {
          list.value = [];
          CareerApiService.SkillSearch(newItem.value).then((res)=>{
              showdropdown.value = true;
              for (let i = 0; i < res.data.skills.length; i++) {
                  list.value.push(res.data.skills[i])
              }
          })
        }
        })
      return {
        spinner,
        error,
        error2,
        empty,
        router,
        extracted_text,
        selectedFile,
        preview,
        item_id,
        numberItem,
        newItem,
        newItemId,
        list,
        showdropdown,
        setTitle,
        cvId,
        stages,
        stage,
        parsingFileName,
        startFeed,
        stopFeed
      }
    },
    data() {
      return {
        items: [], // skills only now — everything else has its own real shape
        isDragOver: false,
        currentStep: 0,
        stepsUnlocked: false,
        steps: [
          { key: 1, label: 'Personal' },
          { key: 2, label: 'Experience' },
          { key: 3, label: 'Skills' },
          { key: 4, label: 'Certs & Languages' },
          { key: 'review', label: 'Review' },
        ],

        personal: { name: '', title: '', email: '', phone: '', location: '' },
        personalTouched: false,

        experienceItems: [],
        certificateItems: [],
        languageItems: [],
        interestItems: [],
        newInterestText: '',
        manageInterests: false,

        employmentTypes: ['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance', 'Remote', 'Hybrid'],
        proficiencyLevels: ['Native', 'Fluent', 'Professional', 'Intermediate', 'Basic'],

        expModalMode: 'add',
        expDraft: this.blankExpDraft(),
        expError: '',
        certDraft: { name: '', issuer: '', year: '' },
        langDraft: { name: '', proficiency: 'Professional' },

        photoPreviewUrl: null,
        extractedPhotoBase64: null,
        extractedPhotoExt: null,
        photoConfirmPending: false,
        photoSaving: false,
      };
    },
    computed: {
      listThree() {
        return this.items.filter((item) => item.list === 3);
      },
    },
    mounted() {
      if (CareerApiService.file) {
        this.spinner=true;
        this.empty=false;
        this.startFeed(CareerApiService.file.name);
        const file = new FormData();
        file.append('file', CareerApiService.file);
        CareerApiService.DesiredJob(CareerApiService.jobId,localStorage.getItem('user_id'),CareerApiService.jobTitle)
        CareerApiService.UploadCv(localStorage.getItem('user_id'),file,CareerApiService.jobTitle).then((res)=>{
          if (res.status==200) {
            this.cvId = res.data.cv_id
            this.extracted_text = res.data.extracted_text;
            this.populateFromExtracted(this.extracted_text);
            this.applyExtractedPhoto(res.data.extracted_photo_base64, res.data.extracted_photo_ext);
            this.spinner = false; this.stopFeed();
          }
        }).catch((err)=>{
          console.error('CV upload failed:', err);
          this.error = 'Something went wrong while reading your CV. Please try again.';
          this.empty = true;
          this.spinner = false; this.stopFeed();
        })
        CareerApiService.file = '';
      }
      else {
        UserInfoApiService.GetUserCvs(localStorage.getItem('user_id')).then((res)=>{
          if (res.data.user_all_cvs.length!=0) {
            this.cvId = res.data.user_all_cvs[res.data.user_all_cvs.length-1].cvid
            UserInfoApiService.CvGetInfo(this.cvId).then((res)=>{
              if (res.data.cv_info.length!=0 && res.status==200) {
                // Populating from a real DB row is the one place a shape
                // surprise (missing column pre-migration, unexpected null)
                // could throw mid-way and leave spinner stuck true forever —
                // which renders as "stuck re-parsing" even though nothing is
                // actually parsing. Guard it so a failure here always still
                // resolves to a visible state.
                try {
                  this.cvId = res.data.cv_info[0].cvid
                  this.extracted_text = res.data.cv_info[0];
                  this.personal.name = this.extracted_text.name && this.extracted_text.name !== 'None' ? this.extracted_text.name : '';
                  this.personal.title = this.extracted_text.professional_title && this.extracted_text.professional_title !== 'None' ? this.extracted_text.professional_title : '';
                  this.personal.email = this.extracted_text.email && this.extracted_text.email !== 'None' ? this.extracted_text.email : '';
                  this.personal.phone = this.extracted_text.mobile_number && this.extracted_text.mobile_number !== 'None' ? this.extracted_text.mobile_number : '';
                  this.personal.location = this.extracted_text.address && this.extracted_text.address !== 'None' ? this.extracted_text.address : '';

                  this.certificateItems = (this.extracted_text.certificates || []).map((c) => ({
                    id: this.item_id++, name: c.name || '', issuer: c.issuer || '', year: c.year || '',
                  }));
                  this.languageItems = (this.extracted_text.language || []).map((l) => ({
                    id: this.item_id++, name: l.name || '', proficiency: l.proficiency || '',
                  }));
                  this.interestItems = this.extracted_text.interests || [];

                  for (const skill of res.data.cv_skills || []) {
                    this.items.push({id:this.item_id,title:skill.skill,list:3,
                      other_results:[skill.other_results],showDropdown:false,
                      skillid:skill.id, unmatched: !skill.matched, source: skill.source, verified: skill.verified})
                    this.item_id++;
                  }

                  const seenExp = new Set()
                  for (const occ of res.data.cv_occups || []) {
                    if (seenExp.has(occ.occup)) continue
                    seenExp.add(occ.occup)
                    this.experienceItems.push({
                      id: this.item_id++, occupid: occ.id, title: occ.occup,
                      other_results: [occ.other_results], unmatched: !occ.matched,
                      startDate: occ.start_date || '', endDate: occ.end_date || '',
                      employmentType: occ.employment_type || '', company: occ.company || '',
                      description: occ.description || '',
                    })
                  }

                  // An existing CV loaded on a normal visit — the walkthrough
                  // was already completed at some point, so every step is free
                  // to jump to rather than re-locked behind linear progress.
                  this.stepsUnlocked = true;
                  this.loadExistingProfilePhoto();
                  this.spinner = false; this.stopFeed();
                } catch (err) {
                  console.error('Failed to load existing CV info:', err);
                  this.empty = true;
                  this.spinner = false; this.stopFeed();
                }
              }
              else {
                this.empty = true;
                this.spinner=false; this.stopFeed();
              }
            }).catch((err)=>{
              console.error('Failed to fetch CV info:', err);
              this.empty = true;
              this.spinner = false; this.stopFeed();
            })
          }
          else {
            this.empty = true;
            this.spinner = false; this.stopFeed();
          }
        }).catch((err)=>{
          console.error('Failed to fetch user CVs:', err);
          this.empty = true;
          this.spinner = false; this.stopFeed();
        })
      }
    },
    methods: {
      blankExpDraft() {
        return { id: null, occupid: null, title: '', titleConfirmed: false, other_results: [], showDropdown: false,
                 startDate: '', endDate: '', employmentType: '', company: '', description: '' };
      },
      // Shared between the "file already picked on another page" mount path
      // and a fresh re-upload from within the wizard.
      populateFromExtracted(extracted) {
        // A fresh parse always starts the walkthrough at Personal, never
        // wherever currentStep happened to be left (e.g. Review, if this
        // is a re-upload from within an already-completed wizard).
        this.currentStep = 0;
        this.personal.name = extracted.name || '';
        this.personal.title = extracted.professional_title || '';
        this.personal.email = extracted.email || '';
        this.personal.phone = extracted.mobile_number || '';
        this.personal.location = extracted.address || '';

        this.experienceItems = (extracted.experience || []).map((exp) => ({
          id: this.item_id++,
          occupid: exp.id || null,
          title: exp.standardized || exp.user_input || '',
          other_results: exp.other_results || [],
          startDate: exp.start_date || '',
          endDate: exp.end_date || '',
          employmentType: exp.employment_type || '',
          company: exp.company || '',
          description: exp.description || '',
        }));

        this.certificateItems = (extracted.certificates || []).map((c) => ({
          id: this.item_id++, name: c.name || '', issuer: c.issuer || '', year: c.year || '',
        }));
        this.languageItems = (extracted.language || []).map((l) => ({
          id: this.item_id++, name: l.name || '', proficiency: l.proficiency || '',
        }));
        this.interestItems = extracted.interests || [];

        this.items = this.items.filter((item) => item.list !== 3);
        for (const s of extracted.skills || []) {
          this.items.push({
            id: this.item_id++, title: s[0].standardized, list: 3,
            other_results: s[0].other_results, showDropdown: false, skillid: s[0].id,
            unmatched: !s[0].standardized,
          });
        }
      },

      goNext() {
        if (this.currentStep < this.steps.length - 1) this.currentStep++;
      },
      goBack() {
        if (this.currentStep > 0) this.currentStep--;
      },
      goToStep(i) {
        if (this.stepsUnlocked || i <= this.currentStep) this.currentStep = i;
      },
      skillFlag(item) {
        // Was: "is the current title also listed among its OWN alternates" -- backwards.
        // other_results is explicitly the OTHER suggestions, excluding whichever one is
        // actually the match, so this returned "unknown" (1) for nearly every correctly-
        // standardized skill, every time the user saved the Skills step -- confirmed live:
        // python, postgresql, tensorflow, pytorch, javascript all correctly standardized
        // (output/final both set) but is_unknown=true on all of them, silently excluding
        // them from career-path matching entirely ("zero match" despite having the exact
        // skills a role needs). `unmatched` (set from the server's actual match result, on
        // load and on add -- see the earlier free-text/unmatched-indicator work) is the
        // real signal for this, not a guess based on the alternates list.
        return item.unmatched ? 1 : 0;
      },
      onPersonalBlur(field, value) {
        if (!this.cvId) return;
        UserInfoApiService.UpdateUserInfo(this.cvId, { [field]: value });
      },
      saveCurrentStep() {
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
          document.activeElement.blur();
        }
        const key = this.steps[this.currentStep].key;
        if (key === 1) {
          this.personalTouched = true;
          if (!this.personal.name || !this.personal.email) return;
          if (this.cvId) {
            UserInfoApiService.UpdateUserInfo(this.cvId, {
              name: this.personal.name, professional_title: this.personal.title,
              email: this.personal.email, mobile_number: this.personal.phone, address: this.personal.location,
            });
          }
        } else if (key === 3) {
          this.listThree.forEach((item) => {
            if (item.skillid) {
              UserInfoApiService.EditSkills([{ _id: item.skillid, skill: item.title, skill_flag: this.skillFlag(item) }]);
            }
          });
        } else if (key === 4) {
          this.saveCertificatesAndLanguages();
        }
        this.goNext();
      },
      saveCertificatesAndLanguages() {
        if (!this.cvId) return;
        UserInfoApiService.UpdateUserInfo(this.cvId, {
          certificates: this.certificateItems.map((c) => ({ name: c.name, issuer: c.issuer, year: c.year })),
          language: this.languageItems.map((l) => ({ name: l.name, proficiency: l.proficiency })),
          interests: this.interestItems,
        });
      },

      // ---------------------------------------------------------- experience
      openExperienceModal(mode, exp) {
        this.expModalMode = mode;
        // Editing an existing entry: its title was already a confirmed, standardized
        // value from a previous save -- only retyping it (the @input handler below)
        // should re-require a fresh pick.
        this.expDraft = mode === 'edit' && exp ? { ...exp, titleConfirmed: true } : this.blankExpDraft();
        const modalEl = document.getElementById('experienceModal');
        // eslint-disable-next-line no-undef
        (window.bootstrap ? new window.bootstrap.Modal(modalEl) : null)?.show();
      },
      onExpTitleInput() {
        // Search on every keystroke (like the skill modal's watch) -- searching only on
        // blur was the actual bug: onExpTitleBlur also hides the dropdown 200ms after blur,
        // which always fired before the network request's results came back, so suggestions
        // could never actually become visible even when the search succeeded.
        this.expDraft.showDropdown = true;
        if (this.expDraft.title && this.expDraft.title.length > 1) {
          CareerApiService.OccupSearch(this.expDraft.title).then((res) => {
            this.expDraft.other_results = [(res.data.jobs || []).map((j) => j.name)];
          });
        } else {
          this.expDraft.other_results = [];
        }
      },
      onExpTitleBlur() {
        // Delay only so a dropdown click (mousedown.prevent in SuggestionDropdown) can
        // register before the field loses focus and hides it.
        setTimeout(() => { this.expDraft.showDropdown = false; }, 200);
      },
      saveExperienceModal() {
        const draft = this.expDraft;
        if (!draft.title) return;
        // Free text allowed again -- see addNewItem()'s identical note. The dropdown still
        // suggests real matches as you type; picking one is no longer mandatory. What we
        // actually standardized it to (or that we couldn't) still gets recorded either way.
        this.expError = '';
        const details = {
          start_date: draft.startDate, end_date: draft.endDate,
          employment_type: draft.employmentType, company: draft.company, description: draft.description,
        };

        if (this.expModalMode === 'edit') {
          const idx = this.experienceItems.findIndex((e) => e.id === draft.id);
          if (idx !== -1) this.experienceItems.splice(idx, 1, { ...draft });
          if (draft.occupid) {
            UserInfoApiService.EditJobs([{ _id: draft.occupid, occup: draft.title }]);
            UserInfoApiService.EditExperienceDetails([{ _id: draft.occupid, ...details }]);
          }
          document.getElementById('closeExperienceModal').click();
          return;
        }

        // New entry: standardize+insert the title first (existing endpoint),
        // then attach the rich fields via the dedicated details endpoint.
        UserInfoApiService.saveJobs([draft.title], localStorage.getItem('user_id'), this.cvId).then((res) => {
          const created = res.data && res.data.processed_occups && res.data.processed_occups[0];
          const occupid = created ? created.occupid : null;
          const matched = created && created.output;
          this.experienceItems.push({
            id: this.item_id++, occupid, title: matched || draft.title, other_results: [],
            unmatched: !matched,
            startDate: draft.startDate, endDate: draft.endDate, employmentType: draft.employmentType,
            company: draft.company, description: draft.description,
          });
          if (occupid) UserInfoApiService.EditExperienceDetails([{ _id: occupid, ...details }]);
          document.getElementById('closeExperienceModal').click();
        });
      },
      removeExperience(exp) {
        this.experienceItems = this.experienceItems.filter((e) => e.id !== exp.id);
        if (exp.occupid) UserInfoApiService.DeleteOccup(exp.occupid);
      },

      // ---------------------------------------------------------- certificates
      openCertificateModal() {
        this.certDraft = { name: '', issuer: '', year: '' };
        const modalEl = document.getElementById('certificateModal');
        (window.bootstrap ? new window.bootstrap.Modal(modalEl) : null)?.show();
      },
      saveCertificateModal() {
        if (!this.certDraft.name) return;
        this.certificateItems.push({ id: this.item_id++, ...this.certDraft });
        this.saveCertificatesAndLanguages();
      },
      removeCertificate(cert) {
        this.certificateItems = this.certificateItems.filter((c) => c.id !== cert.id);
        this.saveCertificatesAndLanguages();
      },

      // ---------------------------------------------------------- languages
      openLanguageModal() {
        this.langDraft = { name: '', proficiency: 'Professional' };
        const modalEl = document.getElementById('languageModal');
        (window.bootstrap ? new window.bootstrap.Modal(modalEl) : null)?.show();
      },
      saveLanguageModal() {
        if (!this.langDraft.name) return;
        this.languageItems.push({ id: this.item_id++, ...this.langDraft });
        this.saveCertificatesAndLanguages();
      },
      removeLanguage(lang) {
        this.languageItems = this.languageItems.filter((l) => l.id !== lang.id);
        this.saveCertificatesAndLanguages();
      },
      proficiencyPct(level) {
        const map = { native: 100, fluent: 100, professional: 80, intermediate: 55, basic: 30 };
        return map[(level || '').toLowerCase()] || 15;
      },

      // ---------------------------------------------------------- interests
      addInterest() {
        const value = this.newInterestText.trim();
        if (!value) return;
        this.interestItems.push(value);
        this.newInterestText = '';
        this.saveCertificatesAndLanguages();
      },
      removeInterest(i) {
        this.interestItems.splice(i, 1);
        this.saveCertificatesAndLanguages();
      },
      interestIcon,

      // ---------------------------------------------------------- profile photo
      loadExistingProfilePhoto() {
        AuthApiService.getProfile(localStorage.getItem('user_id')).then((res) => {
          if (res.status === 200 && res.data.url) this.photoPreviewUrl = res.data.url;
        }).catch(() => {});
      },
      applyExtractedPhoto(base64, ext) {
        if (base64) {
          this.extractedPhotoBase64 = base64;
          this.extractedPhotoExt = ext || 'jpg';
          this.photoPreviewUrl = `data:image/${this.extractedPhotoExt};base64,${base64}`;
          this.photoConfirmPending = true;
        } else {
          this.loadExistingProfilePhoto();
        }
      },
      dismissExtractedPhoto() {
        this.photoConfirmPending = false;
      },
      confirmExtractedPhoto() {
        if (!this.extractedPhotoBase64) return;
        this.photoSaving = true;
        fetch(`data:image/${this.extractedPhotoExt};base64,${this.extractedPhotoBase64}`)
          .then((r) => r.blob())
          .then((blob) => {
            const formData = new FormData();
            formData.append('file', blob, `cv-photo.${this.extractedPhotoExt}`);
            return AuthApiService.updateProfile(formData, localStorage.getItem('user_id'));
          })
          .then(() => { this.photoConfirmPending = false; })
          .finally(() => { this.photoSaving = false; });
      },
      onManualPhotoSelected(event) {
        const file = event.target.files[0];
        if (!file) return;
        this.photoPreviewUrl = URL.createObjectURL(file);
        this.photoConfirmPending = false;
        const formData = new FormData();
        formData.append('file', file);
        AuthApiService.updateProfile(formData, localStorage.getItem('user_id'));
      },

      // ---------------------------------------------------------- upload / re-upload
      uploadCv(){
        if (localStorage.getItem('user_id')) {
          if (this.selectedFile) {
            CareerApiService.file = this.selectedFile;
          if (CareerApiService.file) {
            this.spinner=true;
            this.empty=false;
            this.startFeed(CareerApiService.file.name);
            const file = new FormData();
            file.append('file', CareerApiService.file);
            let title = CareerApiService.jobTitle;
            CareerApiService.UploadCv(localStorage.getItem('user_id'),file,title,'').then((res)=>{
              if (res.status==200) {
                this.cvId = res.data.cv_id;
                this.extracted_text = res.data.extracted_text;
                this.populateFromExtracted(this.extracted_text);
                this.applyExtractedPhoto(res.data.extracted_photo_base64, res.data.extracted_photo_ext);
                this.spinner = false; this.stopFeed();
              }
            }).catch((err)=>{
              console.error('CV re-upload failed:', err);
              this.error = 'Something went wrong while reading your CV. Please try again.';
              this.empty = true;
              this.spinner = false; this.stopFeed();
            })
          }
          else {
            this.empty = true;
          }
        }
        else {
            this.error = 'Please upload the file first'
        }
        }
        else {
          this.router.push({name:'signin'})
        }
      },
      handleFileSelect(event) {
        const file = event.target.files[0];
        if (file) {
          this.selectedFile = file;
          this.preview = file.name;
          document.getElementById('close').click();
          this.uploadCv();
        }
      },
      handleDrop(event) {
        event.preventDefault();
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
          this.selectedFile = files[0];
          this.preview = files[0].name;
          document.getElementById('close').click();
        }
      },

      // ---------------------------------------------------------- skills (unchanged)
      handleBlur1(variable) {
        // Same fix as skillFlag() -- was gated on other_results even existing (skipping the
        // update entirely for a confident match with no alternates, the common case) and
        // used the same backwards "title is in its own alternates" check otherwise.
        if (variable && variable.skillid) {
          setTimeout(() => {
          setTimeout(() => {
            UserInfoApiService.EditSkills([{'_id':variable.skillid,'skill':variable.title,'skill_flag':variable.unmatched ? 1 : 0}])
            variable.showDropdown = false;
            this.items = this.items.map(item => {
              if (item.id === variable.id) {
                return { ...item, title: variable.title };
              }
              return item;
            });
          }, 200);
        }, 200);
        }
        else {
          variable.showDropdown = false;
        }
      },
      selectOption(option,item) {
        item.title = option;
        item.showDropdown = false;
        if (item.skillid) {
          let skill_flag = 0;
          UserInfoApiService.EditSkills([{'_id':item.skillid,'skill':item.title,'skill_flag':skill_flag}])
        }
      },
      removeSkill(object) {
        this.items = this.items.filter(item => item.id !== object.id)
        if (object.skillid) UserInfoApiService.DeleteSkill(object.skillid);
      },
      setNumber(num) {
        this.numberItem = num;
      },
      addNewItem() {
        // Free text is allowed again -- deliberately not requiring a dropdown pick.
        // Blocking anything not already in our data (an earlier version of this) meant we
        // never even recorded what someone tried to type when it has no match -- exactly
        // the signal worth keeping (input/output/final already exist for this: raw text,
        // what standardization attempted, what the user finally confirmed). The dropdown
        // still helps by suggesting real matches as you type; picking one is just no longer
        // mandatory. Unmatched entries get flagged in the UI (see `unmatched` below) so it's
        // clear they don't count toward career-path matching yet, without losing the entry.
        const exists = this.items.some(item =>
          item.title.toLowerCase() === this.newItem.toLowerCase() && item.list === 3
        );
        if (!exists) {
          let addSkill = [this.newItem]
          UserInfoApiService.saveSkills(addSkill,localStorage.getItem('user_id'),this.cvId).then((res)=>{
              if (res.status==200) {
                document.getElementById('closeModal').click();
                // skillid from the server response -- without it, editing or deleting this
                // skill afterward silently no-ops (every handler below is guarded on
                // `if (item.skillid)`).
                const created = res.data && res.data.processed_skills && res.data.processed_skills[0];
                const skillid = created ? created.skillid : null;
                const matched = created && created.output;
                this.items.push({
                  id: this.item_id, title: matched || addSkill[0], list: 3, skillid,
                  other_results: [], showDropdown: false, unmatched: !matched,
                })
                this.item_id++;
                this.newItem = '';
                this.newItemId = null;
              }
          })
        }
        else {
          this.error2 = "This item has already been added."
        }
        this.showdropdown = false;
      }
    },
  };
  </script>

  <style scoped>
  .tm-cv {
    min-height: 60dvh;
    padding: 8px 0 64px;
  }

  .tm-cv-empty {
    text-align: center;
    padding: 96px 24px;
  }

  .tm-cv-empty__title {
    font-weight: 700;
    margin-bottom: 8px;
  }

  /* ---------------------------------------------- extraction feed */
  .tm-cv-parsing {
    padding: 72px 24px 96px;
  }

  .tm-cv-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 120px 24px;
  }

  .tm-cv-parsing__title {
    font-size: 26px;
    font-weight: 700;
    margin: 10px 0 4px;
  }

  .tm-cv-parsing__progress {
    margin: 26px 0 18px;
  }

  .tm-feed {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .tm-feed__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 14px;
    border-radius: var(--tm-radius-sm);
    font-size: 14px;
    transition: background-color 0.3s var(--tm-ease);
  }

  .tm-feed__row.on { background: var(--tm-surface-sunk); }

  .tm-feed__tick {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    flex: none;
    display: grid;
    place-items: center;
    font-size: 10px;
    font-weight: 700;
    background: var(--tm-border);
    color: var(--tm-ink-faint);
    transition: background-color 0.3s var(--tm-ease), color 0.3s var(--tm-ease);
  }

  .tm-feed__row.done .tm-feed__tick {
    background: var(--tm-have);
    color: #fff;
  }

  .tm-feed__row.on .tm-feed__tick {
    background: var(--tm-accent);
    color: #fff;
    animation: tm-feed-pulse 1.1s var(--tm-ease) infinite;
  }

  @keyframes tm-feed-pulse {
    50% { transform: scale(1.16); }
  }

  .tm-feed__label { color: var(--tm-ink-muted); }
  .tm-feed__row.done .tm-feed__label,
  .tm-feed__row.on .tm-feed__label { color: var(--tm-ink); }

  @media (prefers-reduced-motion: reduce) {
    .tm-feed__row.on .tm-feed__tick { animation: none; }
  }

  .tm-cv-wizard {
    position: relative;
  }

  .tm-cv-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 24px;
  }

  .tm-cv-head__actions {
    display: flex;
    gap: 8px;
  }

  /* Progress rail */
  .tm-cv-sidebar {
    display: flex;
    flex-direction: column;
    gap: 2px;
    /* Vertical padding matches .tm-card's default (26px) so the sidebar's
       first line lands even with the first line of whatever sits in the
       main column, whether that's a card or a flush step wrapper. */
    padding: 26px 14px;
    position: sticky;
    top: 76px;
  }


  .tm-cv-sidebar__step {
    display: flex;
    align-items: center;
    gap: 10px;
    background: transparent;
    border: none;
    border-radius: var(--tm-radius-sm);
    padding: 10px 10px;
    text-align: left;
    color: var(--tm-ink-faint);
    cursor: not-allowed;
    transition: background-color 0.16s var(--tm-ease), color 0.16s var(--tm-ease);
  }

  .tm-cv-sidebar__step:not(:disabled) {
    cursor: pointer;
    color: var(--tm-ink-muted);
  }

  .tm-cv-sidebar__step:not(:disabled):hover {
    background: var(--tm-surface-sunk);
    color: var(--tm-ink);
  }

  .tm-cv-sidebar__step.active {
    background: var(--tm-accent-wash);
    color: var(--tm-accent-deep);
    font-weight: 600;
  }

  .tm-cv-sidebar__num {
    font-size: 0.72rem;
    width: 20px;
    flex: none;
  }

  .tm-cv-sidebar__label {
    font-size: 0.88rem;
  }

  .tm-cv-main { min-width: 0; }

  /* .tm-cv-step is used two ways: as the card itself (Skills/Review, paired
     with .tm-card) and as a plain layout wrapper around cards it doesn't
     own (Personal/Experience/Certs). It must never add its own padding —
     doing so double-pads the card steps and misaligns the plain-wrapper
     steps against the sidebar, which is exactly what was broken. */
  .tm-card.tm-cv-step {
    padding: 26px;
  }

  .tm-section-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
    /* First element in the flush (non-card) steps — carries the top
       offset that would otherwise come from a card's own padding, so it
       lines up with the sidebar and with card-based steps. */
    padding-top: 26px;
  }

  /* Used when the section head sits inside a .tm-card that already
     supplies that top offset via its own padding (Experience). */
  .tm-section-head--in-card { padding-top: 0; }

  .tm-cv-step__empty {
    font-size: 0.88rem;
    color: var(--tm-ink-faint);
    padding: 24px 0;
  }

  .tm-chip-zone {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .tm-chip-item {
    position: relative;
    display: flex;
    align-items: center;
  }
  /* Learned from a course (learning plan): a certificate makes it verified. */
  .tm-chip-item--verified :deep(.tp-pill) { background: #E7F6EF; border-color: #A8DEC6; }
  .tm-chip-item__src { margin-left: 6px; font-size: 11px; font-weight: 700; color: var(--tm-ink-muted); white-space: nowrap; }
  .tm-chip-item--verified .tm-chip-item__src { color: #0B7A55; }

  /* Recorded but not standardized -- distinct from removed entirely (the earlier,
     reverted behavior). Reuses the app's own "partial" coverage-scale color (amber) --
     not wrong, just not confirmed yet, which is a different signal than the red "gap"
     tokens elsewhere mean. An earlier version of this used `background: transparent`,
     which against the page's own white background rendered as an invisible/empty
     pill -- worse than no indicator at all. */
  .tm-chip-item--unmatched :deep(.tp-pill) {
    background: var(--tm-partial-wash);
    border: 1px dashed var(--tm-partial);
    color: var(--tm-partial);
  }

  .tm-unmatched-badge {
    display: inline-block;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: var(--tm-partial);
    background: var(--tm-partial-wash);
    border: 1px dashed var(--tm-partial);
    border-radius: var(--tm-radius-pill, 999px);
    padding: 1px 7px;
    margin-left: 8px;
    vertical-align: middle;
  }

  /* Review reuses each real step's card design read-only, just stacked
     vertically instead of laid out side by side. */
  .tm-review-stack {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .tm-review-personal {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .tm-review-personal__photo {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid var(--tm-border);
    flex: none;
  }

  .tm-review-personal__photo--placeholder {
    display: grid;
    place-items: center;
    background: var(--tm-surface-sunk);
    font-size: 1.4rem;
    color: var(--tm-ink-faint);
  }

  .tm-review-personal__name { font-weight: 700; color: var(--tm-ink); font-size: 1.05rem; }

  /* Plain read-only text in the same grid position an input would sit —
     keeps the field-grid's spacing/alignment without looking editable. */
  .tm-review-value {
    margin: 0;
    padding: 2px 0;
    color: var(--tm-ink-soft);
    font-size: 0.95rem;
  }

  .tm-cv-nav {
    display: flex;
    justify-content: space-between;
    margin-top: 24px;
  }

  .tm-modal {
    background: var(--tm-surface);
    color: var(--tm-ink-soft);
    border-radius: var(--tm-radius-md);
    border: 1px solid var(--tm-border);
  }

  .tm-modal :deep(.modal-header),
  .tm-modal :deep(.modal-footer) {
    border-color: var(--tm-border);
  }

  .tm-modal :deep(.modal-title) {
    color: var(--tm-ink);
  }

  .tm-modal-input-wrap {
    position: relative;
  }

  .tm-dropzone {
    border: 1px dashed var(--tm-border-strong);
    border-radius: var(--tm-radius-md);
    padding: 40px 20px;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;
  }

  .tm-dropzone--active {
    border-color: var(--tm-accent);
    background: var(--tm-accent-glow);
  }

  .tm-dropzone__icon {
    color: var(--tm-ink-muted);
    font-size: 1.4rem;
  }

  /* ---------------------------------------------------------- personal */
  .tm-personal-grid {
    display: grid;
    grid-template-columns: 1fr 280px;
    gap: 20px;
    align-items: start;
  }

  @media (max-width: 780px) {
    .tm-personal-grid { grid-template-columns: 1fr; }
  }

  /* Was referenced everywhere (Personal step, all three add/edit modals)
     but never actually defined — every field just stacked with zero gap,
     which is why labels sat flush against the field above them. */
  .tm-field-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 22px 16px;
  }

  .tm-field-grid__full { grid-column: 1 / -1; }

  @media (max-width: 560px) {
    .tm-field-grid { grid-template-columns: 1fr; }
  }

  .tm-required { color: var(--tm-gap); }

  .tm-field--error { border-color: var(--tm-gap) !important; }

  .tm-field-error {
    font-size: 0.82rem;
    color: var(--tm-gap);
  }

  .tm-photo-panel {
    text-align: center;
    background: var(--tm-surface-sunk);
  }

  .tm-photo-panel__frame {
    width: 120px;
    height: 120px;
    margin: 0 auto 16px;
    border-radius: var(--tm-radius-lg);
    background: var(--tm-surface);
    border: 1px solid var(--tm-border);
    overflow: hidden;
    display: grid;
    place-items: center;
  }

  .tm-photo-panel__img { width: 100%; height: 100%; object-fit: cover; }
  .tm-photo-panel__placeholder { font-size: 2.4rem; color: var(--tm-ink-faint); }

  .tm-photo-panel__title { font-size: 1rem; margin-bottom: 2px; }

  .tm-photo-panel__change {
    color: var(--tm-accent);
    font-weight: 600;
    font-size: 0.82rem;
    margin-top: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .tm-photo-confirm {
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid var(--tm-border);
    text-align: left;
  }

  .tm-photo-confirm p {
    font-size: 0.82rem;
    color: var(--tm-ink-muted);
    margin-bottom: 10px;
  }

  .tm-photo-confirm__actions {
    display: flex;
    gap: 8px;
  }

  .tm-photo-confirm__actions .tm-btn-primary,
  .tm-photo-confirm__actions .tm-btn-secondary {
    flex: 1;
    padding: 8px 10px;
    font-size: 0.8rem;
  }

  /* -------------------------------------------------------- experience
     One row per entry inside the section's single card — not a card of
     its own, separated by a rule instead of nested elevation. */
  .tm-exp-list {
    display: flex;
    flex-direction: column;
  }

  .tm-exp-row {
    display: flex;
    gap: 20px;
    padding: 20px 0;
  }

  .tm-exp-row:not(:first-child) { border-top: 1px solid var(--tm-border); }
  .tm-exp-row:first-child { padding-top: 0; }
  .tm-exp-row:last-child { padding-bottom: 0; }

  .tm-exp-row__dates {
    flex: none;
    align-self: flex-start;
    background: var(--tm-surface-sunk);
    border: 1px solid var(--tm-border);
    border-radius: var(--tm-radius-pill);
    padding: 5px 12px;
    font-size: 0.72rem;
    color: var(--tm-ink-muted);
    white-space: nowrap;
  }

  .tm-exp-row__body { flex: 1; min-width: 0; }
  .tm-exp-row__title { font-size: 1.05rem; margin-bottom: 4px; }
  .tm-exp-row__meta { font-size: 0.85rem; color: var(--tm-ink-muted); }
  .tm-exp-row__desc { font-size: 0.9rem; margin-top: 10px; color: var(--tm-ink-soft); }

  .tm-exp-row__actions {
    display: flex;
    gap: 6px;
    margin-top: 14px;
  }

  .tm-icon-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: none;
    border-radius: var(--tm-radius-sm);
    background: transparent;
    color: var(--tm-ink-muted);
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .tm-icon-action:hover { background: var(--tm-surface-sunk); color: var(--tm-ink); }
  .tm-icon-action--danger:hover { color: var(--tm-gap); }

  .tm-add-dashed {
    width: 100%;
    border: 1px dashed var(--tm-border-strong);
    border-radius: var(--tm-radius-md);
    background: transparent;
    padding: 14px;
    color: var(--tm-ink-muted);
    font-weight: 600;
    font-size: 0.88rem;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease;
  }

  .tm-add-dashed:hover { border-color: var(--tm-accent); color: var(--tm-accent); }

  /* -------------------------------------------------------- certificates
     Same treatment as experience — rows inside one card, not nested cards. */
  .tm-cert-list {
    display: flex;
    flex-direction: column;
  }

  .tm-cert-row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 0;
  }

  .tm-cert-row:not(:first-child) { border-top: 1px solid var(--tm-border); }
  .tm-cert-row:first-child { padding-top: 0; }
  .tm-cert-row:last-child { padding-bottom: 0; }

  .tm-cert-row__body { flex: 1; min-width: 0; }
  .tm-cert-row__name { font-size: 0.95rem; margin-bottom: 2px; }
  .tm-cert-row__meta { font-size: 0.7rem; letter-spacing: 0.04em; color: var(--tm-ink-faint); }

  /* --------------------------------------------- languages / interests */
  .tm-lang-interest-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  @media (max-width: 780px) {
    .tm-lang-interest-grid { grid-template-columns: 1fr; }
  }

  .tm-lang-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px 24px;
  }

  @media (max-width: 500px) {
    .tm-lang-grid { grid-template-columns: 1fr; }
  }

  .tm-lang-item__row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .tm-lang-item__name { font-weight: 600; color: var(--tm-ink); }
  .tm-lang-item__level { margin-left: auto; font-size: 0.82rem; color: var(--tm-ink-muted); }
  .tm-lang-item__remove { width: 24px; height: 24px; }

  .tm-interest-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .tm-interest-chip {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 92px;
    padding: 14px 8px;
    background: var(--tm-surface);
    border: 1px solid var(--tm-border);
    border-radius: var(--tm-radius-md);
    font-size: 0.76rem;
    text-align: center;
    color: var(--tm-ink-soft);
  }

  .tm-interest-chip__remove {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: none;
    background: var(--tm-gap);
    color: #fff;
    font-size: 9px;
    display: grid;
    place-items: center;
    cursor: pointer;
  }

  .tm-interest-add {
    display: flex;
    gap: 8px;
    margin-top: 16px;
  }

  .tm-manage-interests {
    width: 100%;
    margin-top: 14px;
  }
  </style>
